import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationApi } from '../api/notifications';
import { Notification } from '../types';

const POLL_MS = 30000;

// Keep in sync with the fixed rail width in Sidebar.css (.sidebar width).
const SIDEBAR_WIDTH = 272;
const GAP = 8;

type PanelPos = { top: number; left?: number; right?: number };

const NotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [items, setItems] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const [pos, setPos] = useState<PanelPos>({ top: 0, left: 0 });
  const [isDesktop, setIsDesktop] = useState<boolean>(() =>
    typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches,
  );

  // Position the panel with viewport-fixed coords so it escapes the narrow
  // fixed sidebar instead of being clipped / straddling the rail edge.
  const updatePos = useCallback(() => {
    const btn = btnRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    if (isDesktop) {
      // drop it beside the rail, fully onto the main content
      setPos({ top: r.bottom + GAP, left: SIDEBAR_WIDTH + GAP });
    } else {
      // align to the right edge under the bell
      setPos({ top: r.bottom + GAP, right: Math.max(GAP, window.innerWidth - r.right) });
    }
  }, [isDesktop]);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const onChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const loadCount = useCallback(async () => {
    try {
      const { data } = await notificationApi.unreadCount();
      setCount(data.count);
    } catch { /* silent */ }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await notificationApi.list();
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCount();
    const id = setInterval(loadCount, POLL_MS);
    return () => clearInterval(id);
  }, [loadCount]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  // Reposition on viewport changes while open.
  useEffect(() => {
    if (!open) return;
    const onLayout = () => updatePos();
    window.addEventListener('resize', onLayout);
    window.addEventListener('scroll', onLayout, true);
    return () => {
      window.removeEventListener('resize', onLayout);
      window.removeEventListener('scroll', onLayout, true);
    };
  }, [open, updatePos]);

  const toggle = async () => {
    const next = !open;
    if (next) updatePos();
    setOpen(next);
    if (next) await loadList();
  };

  const onItemClick = async (n: Notification) => {
    if (!n.readFlag) {
      try { await notificationApi.markOneRead(n.id); } catch {}
    }
    setOpen(false);
    if (n.machineId) {
      navigate(`/machines/${n.machineId}?tab=tickets`);
    }
    loadCount();
  };

  const onMarkAll = async () => {
    try {
      await notificationApi.markAllRead();
      setCount(0);
      setItems(items.map((i) => ({ ...i, readFlag: true })));
    } catch {}
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        aria-label="Powiadomienia"
        title="Powiadomienia"
        style={{
          background: 'none', border: 'none', padding: '0.4rem',
          cursor: 'pointer', position: 'relative', borderRadius: 8,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--color-text-secondary)' }}>
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {count > 0 && (
          <span style={{
            position: 'absolute', top: 2, right: 2,
            background: 'var(--color-danger)', color: '#fff',
            fontSize: '0.625rem', fontWeight: 700,
            minWidth: 16, height: 16, borderRadius: 999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '0 4px', lineHeight: 1,
          }}>{count > 99 ? '99+' : count}</span>
        )}
      </button>

      {open && (
        <div style={{
          position: 'fixed', top: pos.top, left: pos.left, right: pos.right,
          width: 'min(360px, 92vw)', maxHeight: '70vh', overflow: 'auto',
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border-strong)',
          borderRadius: 'var(--radius)',
          boxShadow: 'var(--shadow-lg)',
          zIndex: 300,
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '0.75rem 1rem', borderBottom: '1px solid var(--color-border)',
            background: 'var(--color-surface-alt)',
          }}>
            <strong style={{ fontSize: '0.875rem' }}>Powiadomienia</strong>
            {items.some((i) => !i.readFlag) && (
              <button
                type="button"
                onClick={onMarkAll}
                style={{
                  background: 'none', border: 'none',
                  color: 'var(--color-primary)', cursor: 'pointer',
                  fontSize: '0.75rem', textDecoration: 'underline',
                }}
              >
                Oznacz wszystkie jako przeczytane
              </button>
            )}
          </div>
          {loading ? (
            <div style={{ padding: '1rem', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>Ładowanie…</div>
          ) : items.length === 0 ? (
            <div style={{ padding: '1.5rem 1rem', color: 'var(--color-text-muted)', fontSize: '0.875rem', textAlign: 'center' }}>
              Brak powiadomień
            </div>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {items.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    onClick={() => onItemClick(n)}
                    style={{
                      width: '100%', textAlign: 'left',
                      background: n.readFlag ? 'transparent' : 'var(--color-accent-tint)',
                      border: 'none', borderBottom: '1px solid var(--color-border)',
                      padding: '0.75rem 1rem', cursor: 'pointer',
                      display: 'flex', flexDirection: 'column', gap: '0.25rem',
                    }}
                  >
                    <span style={{
                      fontSize: '0.8125rem', color: 'var(--color-text)',
                      fontWeight: n.readFlag ? 400 : 600,
                    }}>{n.message}</span>
                    <span style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)' }}>
                      {new Date(n.createdAt).toLocaleString('pl-PL')}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
